import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiEdit2, FiTrash2, FiSearch, FiPlus, FiFileText, FiImage } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import DataTable from '../../components/DataTable/DataTable';
import Modal from '../../components/Modal/Modal';
import Input from '../../components/Input/Input';
import api from '../../api';
import './QuestionPapers.css';

const INITIAL_FORM = {
  title: '',
  image_url: ''
};

const QuestionPapers = ({ showAlert, showConfirm }) => {
  const [papers, setPapers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const fetchPapers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/question_papers.php');
      setPapers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showAlert("Sync Error", err.response?.data?.error || "Failed to load question papers.", "confirm");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchPapers();
  }, [fetchPapers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title) {
      showAlert("Required fields", "Please fill in the Question Paper Title.", "confirm");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('image_url', form.image_url || '');

      if (imageFile) {
        formData.append('image', imageFile);
      }

      const headers = { 'Content-Type': 'multipart/form-data' };

      if (editingId) {
        formData.append('id', editingId);
        formData.append('_method', 'PUT');
        await api.post('/question_papers.php', formData, { headers });
        showAlert("Success", "Question paper updated successfully!", "success");
      } else {
        await api.post('/question_papers.php', formData, { headers });
        showAlert("Success", "Question paper added successfully!", "success");
      }

      setShowModal(false);
      setEditingId(null);
      setImageFile(null);
      setForm(INITIAL_FORM);
      fetchPapers();
    } catch (err) {
      showAlert("Process failed", err.response?.data?.error || "Action could not be completed.", "confirm");
    }
  };

  const handleDelete = async (id) => {
    showConfirm("Delete Question Paper", "Are you sure you want to delete this question paper? This action is permanent.", async () => {
      try {
        await api.delete(`/question_papers.php?id=${id}`);
        fetchPapers();
        showAlert("Deleted", "Question paper has been removed.", "success");
      } catch (err) {
        showAlert("Delete failed", err.response?.data?.error || "Could not delete question paper.", "confirm");
      }
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filtered = useMemo(() => {
    let data = papers.filter(p =>
      p.title?.toLowerCase().includes(search.toLowerCase())
    );

    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [papers, search, sortConfig]);

  const columns = [
    { key: 'title', label: 'Paper Title', sortable: true, render: (val, row) => {
      const imgUrl = row.image_url 
        ? (row.image_url.startsWith('http') || row.image_url.startsWith('/') 
          ? row.image_url 
          : `${api.defaults.baseURL}/${row.image_url}`)
        : null;
      return (
        <div className="paper-title-cell">
          {imgUrl && (
            <img 
              className="paper-list-thumb" 
              src={imgUrl} 
              alt="Paper Thumbnail" 
              onError={(e) => { e.target.style.display = 'none'; }} 
            />
          )}
          <div className="paper-icon-circle">
            <FiFileText />
          </div>
          <div className="paper-meta-stack">
            <span className="paper-name">{val}</span>
            <span className="paper-date">Uploaded on: {new Date(row.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      );
    }},
    { key: 'image_url', label: 'Preview/Image Link', render: (val) => {
      if (!val) return <span className="text-muted">No Preview</span>;
      const imgUrl = val.startsWith('http') || val.startsWith('/') 
        ? val 
        : `${api.defaults.baseURL}/${val}`;
      return (
        <a href={imgUrl} target="_blank" rel="noopener noreferrer" className="paper-img-link">
          <FiImage /> View File
        </a>
      );
    }},
    { key: 'actions', label: 'Actions', render: (_, row) => (
      <div className="table-actions">
        <button className="table-action-btn table-action-btn--edit" onClick={() => { setEditingId(row.id); setForm({ ...row }); setImageFile(null); setShowModal(true); }}><FiEdit2 /></button>
        <button className="table-action-btn table-action-btn--delete" onClick={() => handleDelete(row.id)}><FiTrash2 /></button>
      </div>
    )}
  ];

  return (
    <div className="papers-page">
      <div className="page-header">
        <div>
          <h2 className="page-header__title">Question Papers Archive</h2>
          <p className="page-header__sub">Upload and index university semester question papers</p>
        </div>
        <Button variant="primary" icon={<FiPlus />} onClick={() => { setEditingId(null); setForm(INITIAL_FORM); setImageFile(null); setShowModal(true); }}>Add Question Paper</Button>
      </div>

      <Card padding="none">
        <div className="shared-toolbar">
          <div className="shared-search-box">
            <FiSearch className="shared-search-box__icon" />
            <input
              type="text"
              placeholder="Search by paper title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="shared-search-box__input"
            />
          </div>
        </div>
        <DataTable columns={columns} data={filtered} loading={loading} onSort={handleSort} sortConfig={sortConfig} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Edit Question Paper' : 'Add New Question Paper'}>
        <form onSubmit={handleSubmit} className="professional-form">
          <div className="form-grid form-grid--1">
            <Input id="title" name="title" label="Question Paper Title" value={form.title} onChange={handleChange} placeholder="e.g. B.Tech Computer Science - Database Systems (Dec 2025)" required />
            
            <div className="input-group">
              <label className="input-label">Paper File/Image</label>
              <div className="file-uploader">
                <input
                  type="file"
                  id="paper-image-upload"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0] || null)}
                  style={{ display: 'none' }}
                />
                <label htmlFor="paper-image-upload" className="file-uploader__label">
                  <FiImage className="file-uploader__icon" />
                  <span>
                    {imageFile 
                      ? `Selected: ${imageFile.name}` 
                      : (form.image_url ? 'Change Paper File/Image' : 'Choose Paper File/Image')}
                  </span>
                </label>
                {(imageFile || form.image_url) && (
                  <div className="file-uploader__preview">
                    <img
                      src={imageFile 
                        ? URL.createObjectURL(imageFile) 
                        : (form.image_url.startsWith('http') || form.image_url.startsWith('/') 
                          ? form.image_url 
                          : `${api.defaults.baseURL}/${form.image_url}`)}
                      alt="Paper Preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="form-actions-bar">
            <Button type="submit" variant="primary" icon={editingId ? <FiEdit2 /> : <FiPlus />}>{editingId ? 'Save Changes' : 'Add Paper'}</Button>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default QuestionPapers;
